import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from './AuthContext';
import { notifySpaceMembers, notifyMemberRemoved, notifyRoleChanged } from '../utils/notifications/triggers';
import { track } from '../utils/analytics';

export type SpaceRole = 'owner' | 'admin' | 'editor' | 'viewer';

export interface Space {
  id: string;
  name: string;
  owner_id: string;
  created_at: string;
  role: SpaceRole;
  member_count: number;
  icon_url?: string | null;
}

export interface SpaceInvitation {
  id: string;
  space_id: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  token: string;
  invited_by: string;
  expires_at: string;
  created_at: string;
  space_name?: string;
}

export interface SpaceMember {
  user_id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  role: SpaceRole;
  joined_at: string;
  is_owner: boolean;
}

interface SpaceContextType {
  // Current space (null = personal space)
  currentSpace: Space | null;
  setCurrentSpace: (space: Space | null) => void;

  // All spaces the user has access to
  spaces: Space[];
  spacesLoading: boolean;
  refreshSpaces: () => Promise<void>;

  // Space members for current space
  members: SpaceMember[];
  membersLoading: boolean;
  refreshMembers: () => Promise<void>;

  // Filter by member
  filterByMember: string | null; // user_id or null
  setFilterByMember: (userId: string | null) => void;

  // Space CRUD operations
  createSpace: (name: string) => Promise<Space | null>;
  updateSpace: (spaceId: string, updates: { name?: string; icon_url?: string | null }) => Promise<boolean>;
  deleteSpace: (spaceId: string) => Promise<boolean>;
  leaveSpace: (spaceId: string) => Promise<boolean>;

  // Invitation operations
  inviteMember: (email: string, role: 'admin' | 'editor' | 'viewer') => Promise<{ success: boolean; error?: string }>;
  getSpaceInvitations: (spaceId: string) => Promise<SpaceInvitation[]>;
  revokeInvitation: (invitationId: string) => Promise<boolean>;
  getMyPendingInvitations: () => Promise<SpaceInvitation[]>;
  respondToInvitation: (token: string, accept: boolean) => Promise<{ success: boolean; error?: string; spaceName?: string; spaceId?: string; role?: string }>;
  removeMember: (userId: string, spaceId?: string) => Promise<boolean>;
  updateMemberRole: (userId: string, role: 'admin' | 'editor' | 'viewer', spaceId?: string) => Promise<boolean>;

  // Helper to check permissions
  canEdit: boolean;
  canManageMembers: boolean;
  isPersonalSpace: boolean;
}

const SpaceContext = createContext<SpaceContextType | undefined>(undefined);

export function SpaceProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  const [currentSpace, setCurrentSpaceState] = useState<Space | null>(null);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [spacesLoading, setSpacesLoading] = useState(true);
  const [members, setMembers] = useState<SpaceMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [filterByMember, setFilterByMember] = useState<string | null>(null);

  // Track if this is the initial load vs a space transition
  const isInitialMembersLoadRef = useRef(true);

  // Load spaces on mount and when user changes
  const refreshSpaces = useCallback(async () => {
    if (!user) {
      setSpaces([]);
      setSpacesLoading(false);
      return;
    }

    setSpacesLoading(true);
    try {
      // Get spaces where user is owner
      const { data: ownedSpaces, error: ownedError } = await supabase
        .from('spaces')
        .select('*')
        .eq('owner_id', user.id);

      if (ownedError) throw ownedError;

      // Get spaces where user is a member
      const { data: memberSpaces, error: memberError } = await supabase
        .from('space_members')
        .select('space_id, role, spaces(*)')
        .eq('user_id', user.id);

      if (memberError) throw memberError;

      // Combine and format spaces
      const allSpaces: Space[] = [];

      // Add owned spaces
      for (const space of ownedSpaces || []) {
        const { count } = await supabase
          .from('space_members')
          .select('*', { count: 'exact', head: true })
          .eq('space_id', space.id);

        allSpaces.push({
          ...space,
          role: 'owner' as SpaceRole,
          member_count: (count || 0) + 1, // +1 for owner
        });
      }

      // Add member spaces
      for (const membership of memberSpaces || []) {
        const space = membership.spaces as any;
        if (space) {
          const { count } = await supabase
            .from('space_members')
            .select('*', { count: 'exact', head: true })
            .eq('space_id', space.id);

          allSpaces.push({
            ...space,
            role: membership.role as SpaceRole,
            member_count: (count || 0) + 1, // +1 for owner
          });
        }
      }

      // Sort by created_at
      allSpaces.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      setSpaces(allSpaces);
    } catch (error) {
      console.error('Error loading spaces:', error);
    } finally {
      setSpacesLoading(false);
    }
  }, [user]);

  // Load members when current space changes
  const refreshMembers = useCallback(async () => {
    if (!currentSpace) {
      // For personal space, clear members but only after a brief delay
      // to avoid visual flicker when transitioning between spaces
      setMembers([]);
      setMembersLoading(false);
      return;
    }

    // Only show loading state on initial load, not during transitions
    // This prevents the flicker when switching spaces
    if (isInitialMembersLoadRef.current) {
      setMembersLoading(true);
    }

    try {
      const { data, error } = await supabase.rpc('get_space_members', {
        p_space_id: currentSpace.id
      });

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error loading space members:', error);
      setMembers([]);
    } finally {
      setMembersLoading(false);
      isInitialMembersLoadRef.current = false;
    }
  }, [currentSpace]);

  // Set current space and persist to localStorage
  const setCurrentSpace = useCallback((space: Space | null) => {
    setCurrentSpaceState(space);
    setFilterByMember(null); // Reset filter when switching spaces
    if (space) {
      localStorage.setItem('calendar-current-space', space.id);
    } else {
      localStorage.removeItem('calendar-current-space');
    }
  }, []);

  // Restore last space from localStorage on mount
  useEffect(() => {
    const savedSpaceId = localStorage.getItem('calendar-current-space')
      || localStorage.getItem('calendar-default-space');
    if (savedSpaceId && savedSpaceId !== 'personal' && spaces.length > 0) {
      const savedSpace = spaces.find(s => s.id === savedSpaceId);
      if (savedSpace) {
        setCurrentSpaceState(savedSpace);
      }
    }
  }, [spaces]);

  // Load spaces when user changes
  useEffect(() => {
    refreshSpaces();
  }, [refreshSpaces]);

  // Load members when current space changes
  useEffect(() => {
    refreshMembers();
  }, [refreshMembers]);

  // Create a new space
  const createSpace = async (name: string): Promise<Space | null> => {
    if (!user) {
      console.error('Cannot create space: no user logged in');
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('spaces')
        .insert({ name, owner_id: user.id })
        .select()
        .single();

      if (error) {
        console.error('Supabase error creating space:', error.message, error.details, error.hint);
        throw error;
      }

      const newSpace: Space = {
        ...data,
        role: 'owner',
        member_count: 1,
      };

      setSpaces(prev => [...prev, newSpace]);
      track('space_created');
      return newSpace;
    } catch (error) {
      console.error('Error creating space:', error);
      return null;
    }
  };

  // Update space name
  const updateSpace = async (spaceId: string, updates: { name?: string; icon_url?: string | null }): Promise<boolean> => {
    try {
      const payload: Record<string, any> = {};
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.icon_url !== undefined) payload.icon_url = updates.icon_url;

      const { error } = await supabase
        .from('spaces')
        .update(payload)
        .eq('id', spaceId);

      if (error) throw error;

      setSpaces(prev => prev.map(s => s.id === spaceId ? { ...s, ...payload } : s));
      if (currentSpace?.id === spaceId) {
        setCurrentSpaceState(prev => prev ? { ...prev, ...payload } : null);
      }
      return true;
    } catch (error) {
      console.error('Error updating space:', error);
      return false;
    }
  };

  // Delete a space
  const deleteSpace = async (spaceId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('spaces')
        .delete()
        .eq('id', spaceId);

      if (error) throw error;

      setSpaces(prev => prev.filter(s => s.id !== spaceId));
      if (currentSpace?.id === spaceId) {
        setCurrentSpace(null);
      }
      return true;
    } catch (error) {
      console.error('Error deleting space:', error);
      return false;
    }
  };

  // Leave a space (as member)
  const leaveSpace = async (spaceId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('space_members')
        .delete()
        .eq('space_id', spaceId)
        .eq('user_id', user.id);

      if (error) throw error;

      setSpaces(prev => prev.filter(s => s.id !== spaceId));
      if (currentSpace?.id === spaceId) {
        setCurrentSpace(null);
      }
      return true;
    } catch (error) {
      console.error('Error leaving space:', error);
      return false;
    }
  };

  // Invite a member by email
  const inviteMember = async (
    email: string,
    role: 'admin' | 'editor' | 'viewer'
  ): Promise<{ success: boolean; error?: string }> => {
    if (!currentSpace || !user) {
      return { success: false, error: 'No space selected' };
    }

    const normalizedEmail = email.toLowerCase().trim();

    // Check if user is already a member
    const existingMember = members.find(m => m.email === normalizedEmail);
    if (existingMember) {
      return { success: false, error: 'This person is already a member' };
    }

    // Check if it's the owner's email
    if (normalizedEmail === user.email) {
      return { success: false, error: "You can't invite yourself" };
    }

    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7); // 7-day expiry

      const { error } = await supabase
        .from('space_invitations')
        .insert({
          space_id: currentSpace.id,
          email: normalizedEmail,
          role,
          invited_by: user.id,
          expires_at: expiresAt.toISOString(),
        });

      if (error) {
        if (error.code === '23505') {
          return { success: false, error: 'This person already has a pending invitation' };
        }
        throw error;
      }

      // Send invitation email (fire-and-forget — don't block on failure)
      const inviterName = user.user_metadata?.display_name || user.email;
      supabase.functions.invoke('send-invite-email', {
        body: {
          to: normalizedEmail,
          spaceName: currentSpace.name,
          role,
          invitedByName: inviterName,
        },
      }).catch((err) => console.error('Failed to send invite email:', err));

      track('invite_sent', { role });
      return { success: true };
    } catch (error) {
      console.error('Error inviting member:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create invitation' };
    }
  };

  // Get pending invitations for a space (for owners/admins)
  const getSpaceInvitations = async (spaceId: string): Promise<SpaceInvitation[]> => {
    try {
      const { data, error } = await supabase
        .from('space_invitations')
        .select('*')
        .eq('space_id', spaceId)
        .gte('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching invitations:', error);
      return [];
    }
  };

  // Revoke a pending invitation
  const revokeInvitation = async (invitationId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('space_invitations')
        .delete()
        .eq('id', invitationId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error revoking invitation:', error);
      return false;
    }
  };

  // Get pending invitations for the current user (invitee side)
  const getMyPendingInvitations = async (): Promise<SpaceInvitation[]> => {
    if (!user?.email) return [];

    try {
      const { data, error } = await supabase
        .from('space_invitations')
        .select('*, spaces(name)')
        .eq('email', user.email.toLowerCase())
        .gte('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map((inv: any) => ({
        ...inv,
        space_name: inv.spaces?.name,
      }));
    } catch (error) {
      console.error('Error fetching my invitations:', error);
      return [];
    }
  };

  // Accept or decline an invitation
  const respondToInvitation = async (
    token: string,
    accept: boolean
  ): Promise<{ success: boolean; error?: string; spaceName?: string; spaceId?: string; role?: string }> => {
    try {
      // Verify the invitation belongs to the current user
      const { data: invitation, error: lookupError } = await supabase
        .from('space_invitations')
        .select('email')
        .eq('token', token)
        .single();

      if (lookupError || !invitation) {
        return { success: false, error: 'Invitation not found or expired' };
      }

      if (invitation.email !== user?.email?.toLowerCase()) {
        return { success: false, error: 'This invitation was sent to a different email address' };
      }

      if (accept) {
        const { data, error } = await supabase.rpc('accept_invitation', { p_token: token });
        if (error) throw error;
        if (data?.success) {
          await refreshSpaces();
          // Trigger notification that member joined
          await notifySpaceMembers({
            type: 'member_joined',
            spaceId: data.space_id,
            actorId: user!.id,
            actorName: user!.user_metadata?.display_name || user!.email || 'Someone',
          });
          return { success: true, spaceName: data.space_name, spaceId: data.space_id, role: data.role };
        }
        return { success: false, error: data?.error || 'Failed to accept invitation' };
      } else {
        // Decline — just delete the invitation
        const { error } = await supabase
          .from('space_invitations')
          .delete()
          .eq('token', token);
        if (error) throw error;
        return { success: true };
      }
    } catch (error) {
      console.error('Error responding to invitation:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to process invitation' };
    }
  };

  // Remove a member
  // spaceId defaults to the current space. Settings lists every space the user
  // belongs to, so it needs to name one explicitly.
  const removeMember = async (userId: string, spaceId?: string): Promise<boolean> => {
    const target = spaceId
      ? spaces.find(s => s.id === spaceId)
      : currentSpace;
    if (!target) return false;

    try {
      // Get member email before deletion
      const member = members.find(m => m.user_id === userId);

      const { error } = await supabase
        .from('space_members')
        .delete()
        .eq('space_id', target.id)
        .eq('user_id', userId);

      if (error) throw error;

      // `members` only ever holds the current space's roster
      if (target.id === currentSpace?.id) {
        setMembers(prev => prev.filter(m => m.user_id !== userId));
      }

      // Notify the removed member
      if (member) {
        await notifyMemberRemoved(member.email, target.name);
      }

      return true;
    } catch (error) {
      console.error('Error removing member:', error);
      return false;
    }
  };

  // Update member role
  const updateMemberRole = async (userId: string, role: 'admin' | 'editor' | 'viewer', spaceId?: string): Promise<boolean> => {
    const target = spaceId
      ? spaces.find(s => s.id === spaceId)
      : currentSpace;
    if (!target) return false;

    try {
      // Get member info before updating
      const member = members.find(m => m.user_id === userId);
      const oldRole = member?.role;

      const { error } = await supabase
        .from('space_members')
        .update({ role })
        .eq('space_id', target.id)
        .eq('user_id', userId);

      if (error) throw error;

      if (target.id === currentSpace?.id) {
        setMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role } : m));
      }

      // Notify the member of role change
      if (member && oldRole) {
        await notifyRoleChanged(member.email, target.name, oldRole, role);
      }

      return true;
    } catch (error) {
      console.error('Error updating member role:', error);
      return false;
    }
  };

  // Computed permissions
  const isPersonalSpace = currentSpace === null;
  const canEdit = isPersonalSpace || ['owner', 'admin', 'editor'].includes(currentSpace?.role || '');
  const canManageMembers = isPersonalSpace || ['owner', 'admin'].includes(currentSpace?.role || '');

  return (
    <SpaceContext.Provider value={{
      currentSpace,
      setCurrentSpace,
      spaces,
      spacesLoading,
      refreshSpaces,
      members,
      membersLoading,
      refreshMembers,
      filterByMember,
      setFilterByMember,
      createSpace,
      updateSpace,
      deleteSpace,
      leaveSpace,
      inviteMember,
      getSpaceInvitations,
      revokeInvitation,
      getMyPendingInvitations,
      respondToInvitation,
      removeMember,
      updateMemberRole,
      canEdit,
      canManageMembers,
      isPersonalSpace,
    }}>
      {children}
    </SpaceContext.Provider>
  );
}

export function useSpace() {
  const context = useContext(SpaceContext);
  if (context === undefined) {
    throw new Error('useSpace must be used within a SpaceProvider');
  }
  return context;
}
